import {assign, send} from 'xstate'
import {nextStep} from 'utils/step_utils'
import i18n from 'i18n/config'
import PriceSummaryCalculator from 'lib/price_summary_calculator'
import ApiFetcher from 'lib/api_fetcher'
import loadScript from "lib/load_script"

const stepName  = 'iframeStep'

const waitForExperticketReady = ({ timeoutMs = 15000, intervalMs = 50 } = {}) => {
  return new Promise((resolve, reject) => {
    const start = Date.now()

    const tick = () => {
      const ready =
        window.Experticket &&
        window.Experticket.Widget &&
        typeof window.Experticket.Widget.Load === "function"

      if (ready) return resolve()

      if (Date.now() - start > timeoutMs) {
        return reject(new Error("Experticket no estuvo listo dentro del timeout"))
      }

      setTimeout(tick, intervalMs)
    }

    tick()
  })
}

async function initExperticket({
  containerId,
  onCartUpdated,
  onCartPurchased,
  onCartCancelled,
} = {}) {
  // Evitar doble init
  // if (window._experticketInitialized === true) return

  await waitForExperticketReady()

  window._experticketInitialized = true

  let lastToken = null

  const widget = window.Experticket.Widget.Load(containerId)
    .OnCartUpdated((products, token) => {
      lastToken = token ?? lastToken
      onCartUpdated?.(products, token)
    })
    .OnCartPurchased((products, token) => {
      lastToken = token ?? lastToken
      onCartPurchased?.(products, token)
    })
    .OnCartCancelled(() => {
      lastToken = null
      onCartCancelled?.()
    })

  return { widget, getLastToken: () => lastToken }
}

const setIframeUrl = assign({iframe_url: (ctx, e) => {
  const event = ctx.event

  const params = {
    provider_id: event.params.provider_id,
    ticket_enclosure_id: event.params.ticket_enclosure_id,
    session_id: event.params.session_id
  }

  const queryString = new URLSearchParams(params).toString()

  return `${ctx.endpoints.experticket_iframe}?${queryString}`
}})

const setLoadScriptError = assign({error: i18n.t('validations:load_script_error')})

const initTickets = assign({tickets: []})

const setTickets = assign({tickets: (ctx, e) => {
  const products = e.products

  if (!products || products.length <= 0) {
    return null
  }

  return products.map((product) => {
    return {
      id: product.id,
      name: product.name,
      qty: 1,
      base_price: product.finalPrice,
      additional_fee: ctx.applied_fee,
      total_price: ctx.applied_fee + product.finalPrice
    }
  })
}})

const setPriceSummary = assign({priceSummary: (ctx) => {
  let summary = new PriceSummaryCalculator({ticket_types: ctx.tickets, currency: ctx.currency}).call()
  const {total_base_price, total_discount, total_additional_fee, total_final_price} = summary

  return Object.assign(ctx.priceSummary, {
    ticketsStep: {
      base_price: total_base_price,
      discount: total_discount,
      additional_fee: total_additional_fee,
      total_price: total_final_price
    }
  })
}})

const setItems = assign({items: (ctx, e) => e.products})

const setNextReady = assign({next_ready: (ctx, e) => e.products && e.products.length > 0 ? true : false})
const setNextUnready = assign({next_ready: (ctx, e) => false})
const setReservationReady = assign({reservation_ready: (ctx, e) => e.products && e.products.length > 0 ? true : false})
const setReservationUnready = assign({reservation_ready: (ctx, e) => false})
const setToken = assign({token: (ctx, e) => e.token})
const bumpIframeKey = assign({
  iframe_key: (ctx) => ctx.iframe_key + 1
});

const isEventReady = (ctx) => !ctx.sessionId

const iframeModule = (config) => {
  return {
    initial: 'init',
    states: {
      init: {
        on: {
          '': [
            {
              target: 'loadScript',
              actions: ['startSpinner', bumpIframeKey, initTickets, setPriceSummary, setIframeUrl, setNextUnready, setReservationUnready]
            }
          ],
        },
      },
      loadScript: {
        invoke: {
          id: "load_script",
          src: (ctx) => loadScript(ctx.iframe_url),
          onDone: [
            {
              target: 'initWidget'
            }
          ],
          onError: {
            target: '#purchase.error',
            actions: [setLoadScriptError, 'stopSpinner']
          }
        },
      },
      initWidget: {
        invoke: {
          id: "initExperticket",
          src: (ctx, ev) => (callback, onReceive) => {
            return initExperticket({
              containerId: ctx.iframe_container_id,
              onCartUpdated: (products, token) => {
                callback({ type: "CART_UPDATED", products, token })
              },
              onCartPurchased: (products, token) => {
                callback({ type: "CART_PURCHASED", products, token })
              },
              onCartCancelled: () => {
                callback({ type: "CART_CANCELLED" })
              },
            })
          },

          onDone: {
            target: "waitingIframeResponse",
            actions: [
              'stopSpinner',
              assign({
                experticket: (_ctx, ev) => ev.output, // { widget, getLastToken }
              }),
            ]
          },
          onError: {
            target: '#purchase.error',
            actions: [setLoadScriptError, 'stopSpinner']
          }
        },
      },
      waitingIframeResponse: {
      //   invoke: {
      //     id: "iframeMessages",
      //     src: (ctx) => (sendBack) => {
      //       // const allowedOrigins = ["https://EL_ORIGEN_REAL_DEL_IFRAME"];
      //
      //       const onMessage = (event) => {
      //         // if (!allowedOrigins.includes(event.origin)) return;
      //
      //         const data = event.data
      //
      //         if (Array.isArray(data) && data[0] === "resizeHeight") {
      //           const height = Number(data[1])
      //           if (!Number.isFinite(height)) return
      //
      //           sendBack({ type: "IFRAME.RESIZE_HEIGHT", height, raw: data })
      //         }
      //       };
      //
      //       window.addEventListener("message", onMessage)
      //       return () => window.removeEventListener("message", onMessage)
      //     },
      //   },
      //   on: {
      //     "IFRAME.RESIZE_HEIGHT": {
      //       actions: [
      //         (ctx, ev) => {
      //           console.log("ACTION IFRAME RESIZE", ev.height);
      //           const iframe = document.getElementById(ctx.iframe_container_id)
      //           console.log(iframe)
      //           if (iframe) iframe.style.height = `${ev.height}px`;
      //         },
      //       ],
      //     },
      //   },
      }
    },
    on: {
      // NEXT_STEP_CLICK: [
      //   {
      //     target: '#purchase.' + nextStep(config, stepName)
      //   }
      // ],
      CART_UPDATED: {
        actions: [setTickets, setPriceSummary, setItems, setNextReady, setReservationReady, setToken]
        // actions: [setOneboxCart, setTickets, setNextReady, setReservationReady]
      },
    }
  }
}

export default iframeModule
